import { GridProps } from '@mui/material';
import { PutEffect, put } from 'redux-saga/effects';
import {
  AppState,
  findDashboardConciliationsStateRequested,
  dashboardConciliationsStateSelector,
  findMastersStatus2Requested,
  findSovosImportStatusesRequested,
  dashboardSovosImportStatusesSelector,
  dashboardMastersStatus2Selector,
  findDashboardConciliationsStateCardsRequested,
  findDashboardTotalsCardsRequested,
  findDashboardClosureEvolutionRequested,
} from '../../state';

import { AppPermissions, RemoteResource } from '../../types';

import { MasterStatus, FiscalStatus, DashboardClosureEvolution } from './incorporations';

import { CountryConciliation } from './CountryConciliation';
import DashboardStatusCards from './DashboardStatusCards';
import DashboardTotalsCards from './DashboardTotalCards';

type DashCtx = {
  singleCountryDashboard: boolean; // tipo de perfil (no FULL)
  hasFullCountryAccess: boolean; // FULL
  activeCountry?: string | null; // país seleccionado/activo
};
interface DashboardItem {
  component: JSX.Element;
  gridProps?: GridProps;
  when?: (ctx: DashCtx) => boolean;
}

interface DashboardConfiguration {
  permissions: AppPermissions[];
  request?: PutEffect;
  component?: JSX.Element;
  resource?: (state: AppState) => RemoteResource<unknown[]>;
  gridProps?: GridProps;
  singleCountryOrder: number;
  order: number;
  items?: DashboardItem[];
  when?: (ctx: DashCtx) => boolean;
}

const DashboardsConfiguration: Record<string, DashboardConfiguration> = {
  CONCILIATIONS_STATE: {
    component: <CountryConciliation />,
    permissions: [AppPermissions.CONCILIATIONS_DASHBOARD],
    request: put(findDashboardConciliationsStateRequested()),
    resource: dashboardConciliationsStateSelector,
    gridProps: { xs: 12, sm: 10, lg: 4 },
    singleCountryOrder: 1,
    order: 1,
    // SOLO FULL ve la tabla multipaís
    when: ({ hasFullCountryAccess }) => hasFullCountryAccess,
  },

  CONCILIATIONS_STATE_CARDS: {
    component: <DashboardStatusCards />,
    permissions: [AppPermissions.CONCILIATIONS_DASHBOARD],
    request: put(findDashboardConciliationsStateCardsRequested()),
    gridProps: { xs: 12, sm: 10, lg: 12 },
    singleCountryOrder: 4,
    order: 4,
    // Se ven cuando hay país activo (FULL seleccionó uno, o perfil single-country ya lo setea)
    when: ({ activeCountry }) => Boolean(activeCountry),
  },

  TOTALS_CARDS: {
    permissions: [AppPermissions.CONCILIATIONS_DASHBOARD],
    request: put(findDashboardTotalsCardsRequested()),
    gridProps: { xs: 12, sm: 10, lg: 12 },
    singleCountryOrder: 6,
    order: 6,
    when: ({ activeCountry }) => Boolean(activeCountry),
    items: [
      { component: <DashboardTotalsCards lapse="yesterday" />, gridProps: { xs: 12, lg: 4 } },
      { component: <DashboardTotalsCards lapse="currentMonth" />, gridProps: { xs: 12, lg: 4 } },
      { component: <DashboardTotalsCards lapse="lastMonth" />, gridProps: { xs: 12, lg: 4 } },
    ],
  },

  FISCAL_STATUS: {
    component: <FiscalStatus />,
    permissions: [AppPermissions.DASHBOARD_FISCAL_IMPORT],
    request: put(findSovosImportStatusesRequested()),
    resource: dashboardSovosImportStatusesSelector,
    gridProps: { xs: 12, sm: 10, lg: 5 },
    singleCountryOrder: 2,
    order: 2,
  },

  MASTER_STATUS: {
    component: <MasterStatus />,
    permissions: [AppPermissions.DASHBOARD_MASTER_IMPORT],
    request: put(findMastersStatus2Requested()),
    resource: dashboardMastersStatus2Selector,
    gridProps: { xs: 12, sm: 10, lg: 5 },
    singleCountryOrder: 3,
    order: 3,
  },

  CLOSURE_EVOLUTION_CARD: {
    component: <DashboardClosureEvolution />,
    permissions: [AppPermissions.CONCILIATIONS_DASHBOARD],
    request: put(findDashboardClosureEvolutionRequested()),
    gridProps: { sm: 10, lg: 12 },
    singleCountryOrder: 7,
    order: 7,
  },
};

const Dashboards = Object.entries(DashboardsConfiguration).map(([name, config]) => ({
  ...config,
  name,
}));

export default Dashboards;
